import { useState, useEffect, useCallback } from "react";
import { useAudioRecorder } from "react-audio-voice-recorder";

const useRecorder = () => {
  const [audioURL, setAudioURL] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [audioError, setError] = useState("");

  const recorder = useAudioRecorder({
    downloadFileExtension: "mp3",
    audioTrackConstraints: {
      noiseSuppression: true,
      echoCancellation: true,
    },
    onNotAllowedOrFound: (err) => {
      console.error("Audio permission error:", err);
      setError("Audio Permission Denied");
    },
  });

  const isRecording = recorder.isRecording;
  const isPaused = recorder.isPaused;

  useEffect(() => {
    if (!recorder.recordingBlob) return;

    const blobURL = URL.createObjectURL(recorder.recordingBlob);
    setAudioURL(blobURL);

    const file = new File([recorder.recordingBlob], "audio-file.mp3", {
      type: "audio/mp3",
      lastModified: Date.now(),
    });
    setAudioFile(file);
  }, [recorder.recordingBlob]);

  const startRecording = useCallback(() => {
    recorder.startRecording();
  }, [recorder]);

  const stopRecording = useCallback(() => {
    recorder.stopRecording();
  }, [recorder]);

  const togglePause = useCallback(() => {
    recorder.togglePauseResume();
  }, [recorder]);

  const testMicrophone = useCallback(async () => {
    let stream;
    let audioContext;

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
        },
      });

      const track = stream.getAudioTracks()[0];

      if (!track || track.readyState !== "live") {
        return false;
      }

      audioContext = new (window.AudioContext || window.webkitAudioContext)();

      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.fftSize);

      const timeout = 5000; // wait up to 5 seconds
      const threshold = 0.01; // less strict than 0.02

      const start = performance.now();

      while (performance.now() - start < timeout) {
        analyser.getByteTimeDomainData(dataArray);

        let sum = 0;

        for (let i = 0; i < dataArray.length; i++) {
          const sample = (dataArray[i] - 128) / 128;
          sum += sample * sample;
        }

        const rms = Math.sqrt(sum / dataArray.length);

        // Uncomment while testing
        // console.log("Mic RMS:", rms);

        if (rms >= threshold) {
          return true;
        }

        await new Promise((resolve) => setTimeout(resolve, 30));
      }

      return false;
    } catch (err) {
      console.error("Microphone test failed:", err);
      return false;
    } finally {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      if (audioContext && audioContext.state !== "closed") {
        await audioContext.close();
      }
    }
  }, []);

  const isSilentRecording = async (blob) => {
    const ctx = new AudioContext();

    const buffer = await blob.arrayBuffer();

    const decoded = await ctx.decodeAudioData(buffer);

    const samples = decoded.getChannelData(0);

    let sum = 0;

    for (let i = 0; i < samples.length; i++) {
      sum += samples[i] * samples[i];
    }

    const rms = Math.sqrt(sum / samples.length);

    ctx.close();

    return rms < 0.01;
  };

  return [
    audioURL,
    isRecording,
    startRecording,
    stopRecording,
    audioFile,
    audioError,
    togglePause,
    isPaused,
    testMicrophone,
    isSilentRecording,
    recorder,
    setAudioURL,
  ];
};

export default useRecorder;
